import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Inbox, AlertCircle, CheckCircle, Clock } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface Escalation {
  id: string;
  bot_id: string;
  tenant_id: string;
  reason: string | null;
  summary: unknown;
  sent_to_email: string | null;
  created_at: string | null;
}

const AdminInbox: React.FC = () => {
  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEscalations = async () => {
      try {
        const { data, error } = await supabase
          .from('escalations')
          .select('*')
          .is('campaign_id', null)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setEscalations(data || []);
      } catch (error) {
        console.error('Error fetching escalations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEscalations();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Inbox</h1>
          <p className="text-muted-foreground mt-1">
            Escalated conversations and urgent notifications
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Escalations
              </CardTitle>
              <AlertCircle className="w-4 h-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{escalations.length}</p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Today
              </CardTitle>
              <Clock className="w-4 h-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {escalations.filter(e => {
                  const today = new Date().toDateString();
                  return e.created_at && new Date(e.created_at).toDateString() === today;
                }).length}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Resolved
              </CardTitle>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">0</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Escalations</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-24" />
                ))}
              </div>
            ) : escalations.length === 0 ? (
              <div className="text-center py-12">
                <Inbox className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">Inbox is empty</h3>
                <p className="text-muted-foreground mt-1">
                  No escalated conversations at this time
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {escalations.map((escalation) => (
                  <div
                    key={escalation.id}
                    className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-orange-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <AlertCircle className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <h4 className="font-medium">
                            {escalation.reason || 'Escalation'}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Bot: {escalation.bot_id.slice(0, 8)}... • 
                            Tenant: {escalation.tenant_id.slice(0, 8)}...
                          </p>
                          {escalation.sent_to_email && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Forwarded to: {escalation.sent_to_email}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground">
                          {escalation.created_at && formatDate(escalation.created_at)}
                        </p>
                        <Button variant="outline" size="sm" className="mt-2">
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminInbox;
